import { useCallback, useMemo } from "react";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useSelector } from "react-redux";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL, BASE_API_URL_V2 } from "@/constants";
import { handleError } from "@/utils/handleError";
import { displayOrganizationName } from "@/utils/organizationName";

export type WorkspaceKind =
  | "chat"
  | "report"
  | "matrix"
  | "matrix_template"
  | "report_instance";

function kindToTypeToFetch(kind: WorkspaceKind): string {
  if (kind === "report") return "private_doc_editor_template";
  if (kind === "report_instance") return "private_doc_editor";
  if (kind === "matrix") return "querygrid";
  if (kind === "matrix_template") return "querygrid_template";
  if (kind === "chat") return "chat";
  return "private_doc_editor";
}

/** The react-query key for one kind's list, per org and user. Every consumer
 * of a kind on a page (the sidebar's preload, the history panel's filtered and
 * unfiltered copies, the hub pages) shares it, so they share one in-flight
 * read instead of issuing one each (#12239). */
function workspacesQueryKey(
  kind: WorkspaceKind,
  orgId: string | undefined,
  userId: string | undefined
): readonly [string, string | null, string | null, string] {
  return ["workspaces", orgId ?? null, userId ?? null, kindToTypeToFetch(kind)];
}

/** The read behind a kind's list. `staleTime: 0` keeps it dedupe-only: a
 * remount still reads the server, exactly as the hook always has. No retry and
 * no refetch on focus, matching the plain fetch it replaced. */
function workspacesQueryOptions(
  kind: WorkspaceKind,
  orgId: string | undefined,
  userId: string | undefined
) {
  const typeToFetch = kindToTypeToFetch(kind);
  return {
    queryKey: workspacesQueryKey(kind, orgId, userId),
    queryFn: async (): Promise<{ workspaces: any[]; teams: any[] }> => {
      try {
        return await fetchWorkspacesData(typeToFetch);
      } catch (e) {
        handleError({
          error: e as Error,
          location: `loadWorkspaces for ${typeToFetch}`,
        });
        throw e;
      }
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  } as const;
}

// Shape of a row from GET /chat/conversations (the fields the sidebar needs).
interface ChatConversationListItem {
  conversation_id: string;
  owner_user_id: string;
  title: string | null;
  created_at?: string;
  updated_at?: string;
  // Present on the `scope=all` ladder; null when the chat isn't shared.
  shared_with?: {
    users?: { id: string }[];
    teams?: { id: string; name: string }[];
    organization?: { id: string };
  } | null;
  owner_name?: string | null;
  owner_email?: string | null;
}

// Map a chat conversation into the legacy workspace-item shape that process()
// consumes. process() keys on `_id` (the row id) and `user_id` (to decide
// `isCurrentUser`, which is what lands a row in "My chats"), so a mapping that
// omits them silently drops every conversation from all groups. Title falls
// back to a "New Chat" label until backend auto-titling fills it in. (#8147)
//
// `shared_with` is what lands a row in any group OTHER than "My chats" —
// omitting it dropped every shared conversation from the sidebar entirely
// rather than misfiling it. `name` / `email` carry the owner so a shared row
// reads "Shared by Lakshay" instead of "Shared by Unknown". (#10742)
export function mapConversationToWorkspaceItem(c: ChatConversationListItem) {
  return {
    _id: c.conversation_id,
    user_id: c.owner_user_id,
    title: c.title || "New Chat",
    type: "chat",
    created_at: c.created_at,
    updated_at: c.updated_at,
    shared_with: c.shared_with,
    name: c.owner_name,
    email: c.owner_email,
  };
}

async function fetchWorkspacesData(
  typeToFetch: string
): Promise<{ workspaces: any[]; teams: any[] }> {
  // Chat can live in EITHER store during the agentic rollout (#8147): new
  // conversations in chat_conversations, legacy ones in workspaces. Merge both
  // so a user sees all their chats regardless of which backend their org is on;
  // each row's id shape (pgid vs bare uuid) tells WorkspaceHistory which backend
  // to route a click to. Non-chat kinds use the legacy workspaces endpoint.
  //
  // `scope=all` is the full permission ladder — owned plus every chat shared
  // with the user, their teams, or the org. Asking only for `scope=mine` was
  // why "Shared with me" and the org group rendered empty no matter how many
  // grants existed (#10742).
  const isChat = typeToFetch === "chat";
  const workspaceUrls = isChat
    ? [
        `${BASE_API_URL_V2}/api/v2/chat/conversations?scope=all`,
        `${BASE_API_URL}/workspaces?type=chat`,
      ]
    : [`${BASE_API_URL}/workspaces?type=${typeToFetch}`];
  const [teamsRes, ...workspaceRes] = await Promise.all([
    fpFetch(`${BASE_API_URL_V2}/api/v2/users/teams`),
    ...workspaceUrls.map((url) => fpFetch(url)),
  ]);

  let items: any[] = [];
  if (isChat) {
    const [agenticData, legacyData] = await Promise.all([
      workspaceRes[0].json(),
      workspaceRes[1].json(),
    ]);
    items = [
      ...(agenticData.conversations || []).map(mapConversationToWorkspaceItem),
      ...(legacyData.workspaces || []),
    ];
  } else {
    const data = await workspaceRes[0].json();
    items = data.workspaces || [];
  }
  items.sort(
    (a: any, b: any) =>
      new Date(b.updated_at || b.created_at || 0).getTime() -
      new Date(a.updated_at || a.created_at || 0).getTime()
  );

  let teams: any[] = [];
  if (teamsRes?.ok) {
    teams = (await teamsRes.json()) || [];
  }

  return { workspaces: items, teams };
}

/** Mark a kind's list out of date after a change made outside the hook (a
 * template created before navigating away). Every mounted list of that kind,
 * for any org or user, reads again; an unmounted one reads on its next mount,
 * which it does anyway. */
export function invalidateWorkspacesCache(
  client: QueryClient,
  kind: WorkspaceKind
): void {
  const typeToFetch = kindToTypeToFetch(kind);
  void client.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "workspaces" && query.queryKey[3] === typeToFetch,
  });
}

/** Start reading a kind's list before anything shows it (the sidebar does
 * this on chat, matrix and report pages, before the history is expanded). A
 * list mounted while the read is in flight shares it. */
export function preloadWorkspaces(
  client: QueryClient,
  kind: WorkspaceKind,
  orgId: string | undefined,
  userId: string | undefined
): void {
  // prefetchQuery never throws; a failure is reported by the queryFn.
  void client.prefetchQuery(workspacesQueryOptions(kind, orgId, userId));
}

// Simple workspace interfaces
export interface Workspace {
  id: string;
  // The backend workspace type (e.g. "private_doc_editor_template")
  type?: string;
  title: string;
  subtitle: string; // "Shared by me" or "Shared by <name>"
  date: Date;
  description?: string;
  // Schedule status for templates returned by backend: "active", "paused", or null/undefined if none
  schedule_status?: "active" | "paused" | null;
  // Earliest upcoming timed run across the template's active schedules
  // (ISO instant with offset), or null when none (#11426)
  next_run_at?: string | null;
  // Number of reports made from the template (#11426)
  report_count?: number;
  // Pin status for templates
  pinned?: boolean;
  // Folder organization for templates
  folder?: string | null;
  meta?: {
    src?: string;
    src_id?: string;
    shared_with?: any;
    [key: string]: any;
  };
}

export type WorkspaceGroupKind = "own" | "shared" | "team" | "organization";

export interface WorkspaceGroup {
  name: string; // eg. My chats, Team Whatever's chats, etc.
  /** Whose group this is, so a consumer never has to parse `name`. */
  kind: WorkspaceGroupKind;
  /** The short form for a heading: "My", "Shared with me", the team's name,
   *  "Organization". */
  label: string;
  workspaces: Workspace[];
}

// Time-based grouping interface
export interface TimeGroup {
  title: string; // "Today", "Yesterday", "7 days ago", etc.
  workspaces: Workspace[];
}

/**
 * A simplified hook for workspace management
 * @param kind The type of workspace to fetch ("chat" | "report" | "matrix" | "report_instance")
 * @param searchTerm Optional search term to filter workspaces
 * @returns A simplified workspace listing structure
 */
export function useWorkspaces(
  kind: WorkspaceKind,
  searchTerm: string = ""
): {
  groups: WorkspaceGroup[];
  loading: boolean;
  error: Error | null;
  // Resolves once the new rows have landed, so a caller can await it before
  // dropping an optimistic update (the matrix pin toggle does).
  refresh: () => Promise<void>;
} {
  const { userId, userEmail, organization } = useSelector(
    (state: any) => state.userData
  );
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  // Filtering by `searchTerm` happens in `process` below, so a filtered and an
  // unfiltered copy of the same kind share one read.
  const query = useQuery({
    ...workspacesQueryOptions(kind, orgId, userId),
    enabled: !!userEmail,
  });
  const rawWorkspaces = query.data?.workspaces ?? NO_ROWS;
  const userTeams = query.data?.teams ?? NO_ROWS;

  const refresh = useCallback(
    (): Promise<void> =>
      queryClient.invalidateQueries({
        queryKey: workspacesQueryKey(kind, orgId, userId),
      }),
    [queryClient, kind, orgId, userId]
  );

  const groups = useMemo(() => {
    // Early return with empty groups if essential data is missing
    if (!userId || !userEmail) {
      return [];
    }

    return process(
      rawWorkspaces,
      userId,
      userEmail,
      searchTerm,
      kind,
      organization,
      userTeams
    );
  }, [
    rawWorkspaces,
    userId || "",
    userEmail || "",
    searchTerm || "",
    kind,
    organization || null,
    userTeams || [],
  ]);

  return {
    groups,
    // True until the first read lands; a later refetch keeps showing the rows.
    loading: query.isPending,
    error: query.error,
    refresh,
  };
}

// One stable empty list, so `groups` is not recomputed on every render while
// nothing has loaded.
// `any` because the raw rows of the two list endpoints (workspaces and chat
// conversations) have never had a shared type; `process` shapes them.
const NO_ROWS: any[] = [];

export function process(
  rawWorkspaces: any[],
  userId: string,
  userEmail: string,
  searchTerm?: string,
  kind?: "chat" | "report" | "matrix" | "matrix_template" | "report_instance",
  organization?: any,
  userTeams: any[] = []
) {
  // Create ownership groups according to the specified logic
  // Step 2: Create groups
  const myWorkspaces: Workspace[] = [];
  const sharedWithMeWorkspaces: Workspace[] = [];
  const teamWorkspacesMap = new Map<
    string,
    { id: string; name: string; workspaces: Workspace[] }
  >();
  const orgWorkspaces: Workspace[] = [];

  // Initialize with user's teams so they show even if empty
  // Normalize team IDs to strings for consistency
  userTeams.forEach((team) => {
    const teamId = String(team.id || team._id || "");
    if (teamId && team.name) {
      teamWorkspacesMap.set(teamId, {
        id: teamId,
        name: team.name,
        workspaces: [],
      });
    }
  });

  // Filter workspaces that match search term
  const filteredWorkspaces = rawWorkspaces.filter((item) => {
    if (
      searchTerm &&
      !(item.title || "").toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  // Step 2: Create a group for my workspaces and categorize workspaces
  filteredWorkspaces.forEach((item) => {
    const isCurrentUser = item.user_id === userId || item.email === userEmail;
    const shared = item.shared_with || {};
    const date = new Date(
      item.updated_at || item.created_at || new Date().toISOString()
    );

    // Create workspace object
    const workspace = {
      id: item._id,
      type: item.type,
      title: item.title || "",
      subtitle: isCurrentUser
        ? "Shared by me"
        : `Shared by ${item.name?.trim() || item.email || "Unknown"}`,
      date,
      description: item.description || "",
      schedule_status: item.schedule_status ?? null,
      next_run_at: item.next_run_at ?? null,
      report_count: item.report_count ?? 0,
      pinned: item.pinned ?? false,
      folder: item.folder || null,
      meta: {
        ...item.meta,
        shared_with: item.shared_with,
      },
    };

    if (isCurrentUser) {
      myWorkspaces.push({
        ...workspace,
        subtitle: "",
      });
    } else if (shared.users?.some((u: any) => u.id === userId)) {
      sharedWithMeWorkspaces.push(workspace);
    }

    // Step 3 & 4: Process teams - flat map and get distinct team IDs
    // Only add workspaces to teams that the user is actually a member of
    if (shared.teams?.length) {
      // Create a set of user's team IDs for quick lookup (normalize to strings for comparison)
      const userTeamIds = new Set(
        userTeams.map((t: any) => String(t.id || t._id || ""))
      );

      shared.teams.forEach((team: any) => {
        if (!team.id || !team.name) return;

        // Normalize team ID to string for comparison
        const teamId = String(team.id);

        // Only process teams that the user is a member of
        // This ensures workspaces only appear in team groups the user belongs to
        if (!userTeamIds.has(teamId)) return;

        if (!teamWorkspacesMap.has(teamId)) {
          teamWorkspacesMap.set(teamId, {
            id: teamId,
            name: team.name,
            workspaces: [],
          });
        }

        // For all other groups, include "Shared by workspace.name" for each workspace
        teamWorkspacesMap.get(teamId)!.workspaces.push(workspace);
      });
    }

    // Step 5: Create group for organization workspaces
    // Only add to org tab if shared with organization (not link sharing)
    if (shared.organization?.id && !shared.linkSharing?.organization_id) {
      orgWorkspaces.push(workspace);
    }
  });

  // Step 6: Order workspaces by creation time
  myWorkspaces.sort((a, b) => b.date.getTime() - a.date.getTime());
  sharedWithMeWorkspaces.sort((a, b) => b.date.getTime() - a.date.getTime());
  orgWorkspaces.sort((a, b) => b.date.getTime() - a.date.getTime());

  // For team workspaces, sort each team's workspaces
  teamWorkspacesMap.forEach((team) => {
    team.workspaces.sort((a, b) => b.date.getTime() - a.date.getTime());
  });

  // Step 9: Create final groups array
  const result: WorkspaceGroup[] = [];

  const itemSuffix =
    kind === "matrix"
      ? "matrices"
      : kind === "matrix_template"
        ? "templates"
        : kind === "report_instance"
          ? "reports"
          : kind === "report"
            ? "templates"
            : `${kind}s`;

  // Always add my workspaces group (even if empty)
  result.push({
    name: `My ${itemSuffix}`,
    kind: "own",
    label: "My",
    workspaces: myWorkspaces,
  });

  result.push({
    name: `Shared with me`,
    kind: "shared",
    label: "Shared with me",
    workspaces: sharedWithMeWorkspaces,
  });

  // Always add team groups (even if empty)
  teamWorkspacesMap.forEach((team) => {
    result.push({
      name: `${team.name} ${itemSuffix}`,
      kind: "team",
      label: team.name,
      workspaces: team.workspaces,
    });
  });

  // Always add organization group (even if empty) if user is part of an organization
  if (organization?.name) {
    const orgName = `${displayOrganizationName(organization.name)} Organization`;
    result.push({
      name: `${orgName} ${itemSuffix}`,
      kind: "organization",
      label: "Organization",
      workspaces: orgWorkspaces,
    });
  }

  const groups = result;
  return groups;
}

/**
 * Groups workspaces by time periods
 * @param workspaces The workspaces to group
 * @returns An array of time-based groups
 */
export function groupByTime(workspaces: Workspace[]): TimeGroup[] {
  // Define time categories once for both grouping and sorting
  const timeCategories = [
    "Today",
    "Yesterday",
    "Previous 7 days",
    "Previous 30 days",
  ];

  const groups = new Map<string, Workspace[]>();
  const now = new Date();
  const today = new Date(now.setHours(0, 0, 0, 0)).getTime();
  const yesterday = today - 86400000; // 24h in ms
  const week = today - 7 * 86400000;
  const month = today - 30 * 86400000;

  // Group workspaces by time category
  workspaces.forEach((workspace) => {
    const date = workspace.date.getTime();
    let category: string;

    if (date >= today) {
      category = timeCategories[0]; // Today
    } else if (date >= yesterday) {
      category = timeCategories[1]; // Yesterday
    } else if (date >= week) {
      category = timeCategories[2]; // Previous 7 days
    } else if (date >= month) {
      category = timeCategories[3]; // Previous 30 days
    } else {
      // Group by month for older items
      category = workspace.date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
    }

    if (!groups.has(category)) {
      groups.set(category, []);
    }

    groups.get(category)!.push(workspace);
  });

  // Convert to array and sort by time (most recent first)
  return Array.from(groups.entries())
    .map(([title, workspaces]) => ({ title, workspaces }))
    .sort((a, b) => {
      const aIndex = timeCategories.indexOf(a.title);
      const bIndex = timeCategories.indexOf(b.title);

      // Handle predefined categories
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // Month/year categories: newest month first, by the newest item in each
      // (their titles are localized, so they cannot be sorted as text).
      const newest = (ws: Workspace[]): number =>
        Math.max(...ws.map((w) => w.date.getTime()));
      return newest(b.workspaces) - newest(a.workspaces);
    });
}
